import { useMemo } from 'react';
import { LinkText } from '../../../components/ui/link-text.tsx';
import { useToast } from '../../../components/ui/use-toast.tsx';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import { plainTextToLexicalSync } from '../../../lib/rich-text/plain-text-to-lexical.ts';
import type { CrmTaskDTO } from '../../../server/dto/crm-task-dto.ts';
import type { CrmTaskInputDTO } from '../../../server/dto/crm-task-input-dto.ts';
import { api } from '../../../utils/api.ts';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import type { TaskCreatedSource } from '../../analytics/types/analytics-events.ts';
import { DialogStateManager, useDialog } from '../../dialog/dialog-provider.tsx';
import { useDrawers } from '../../drawers/all-drawers.tsx';
import { TaskFormDialog } from './task-form-dialog.tsx';

export type TaskCreateDialogProps = {
    onClose?: () => void;
    taskId?: string;
    links?: Pick<CrmTaskDTO['links'][0], 'modelId' | 'modelType' | 'title'>[];
    description?: string;
    assignedToId?: string;
    source: TaskCreatedSource;
};

export const TaskCreateDialog = DialogStateManager.create<TaskCreateDialogProps>(
    ({ links, onClose, description, assignedToId, source }: TaskCreateDialogProps) => {
        const { visible, remove } = useDialog();
        const sendEvent = useAnalytics();
        const { t } = useLocale('tasks');
        const { toast } = useToast();
        const apiUtils = api.useUtils();
        const { openTaskDetails } = useDrawers();

        const createTaskMutation = api.platoUser.command.tasks.postTasksActions.create.useMutation({
            onSuccess(data: CrmTaskDTO) {
                void Promise.all([
                    apiUtils.platoUser.query.tasks.getManyTasks.getListWithFilters.invalidate(),
                    apiUtils.platoUser.query.tasks.getManyTasks.getUpcomingCustomerTasks.invalidate(),
                    apiUtils.platoUser.query.tasks.getManyTasks.count.invalidate(),
                    apiUtils.platoUser.getManyCustomers.getListWithFilters.invalidate(),
                    apiUtils.platoUser.getManyQuotesOrders.getWithFilters.invalidate(),
                ]);

                if (data.dueDate) {
                    sendEvent('task_created', {
                        task_id: data.taskId,
                        due_date: data.dueDate.toISOString(),
                        assignee_id: data.assignedTo.id,
                        assignor_id: data.assignedBy.id,
                        is_assigned_to_oneself: data.assignedTo.id
                            ? data.assignedTo.id === data.assignedBy.id
                            : false,
                        related_entity_type: data.links[0]?.modelType,
                        related_entity_id: data.links[0]?.modelId,
                        source,
                    });
                }

                toast({
                    icon: 'success',
                    title: t('tasks__toast__created_success_title'),
                    createdLink: (
                        <LinkText
                            as="button"
                            onClick={() => {
                                openTaskDetails(data.taskId);
                            }}
                        />
                    ),
                });
            },
            onError(error) {
                toast({
                    icon: 'warning',
                    title: 'Error',
                    description: error.message,
                });
            },
        });

        const handleClose = (): void => {
            remove();
            onClose?.();
        };

        const initialValues = useMemo<Partial<CrmTaskInputDTO>>(() => {
            const values: Partial<CrmTaskInputDTO> = {};
            if (links) {
                values.links = links;
            }
            if (assignedToId) {
                values.assignedToId = assignedToId;
            }
            if (description) {
                values.description = plainTextToLexicalSync(description) ?? undefined;
            }
            return values;
        }, [links, assignedToId, description]);

        return (
            <TaskFormDialog
                open={visible}
                onClose={handleClose}
                onSubmit={async (values: CrmTaskInputDTO) => {
                    await createTaskMutation.mutateAsync(values);
                }}
                title={t('tasks__form_create_title')}
                initialValues={initialValues}
            />
        );
    },
);
